import React from 'react'
import { Box, Grid, Typography, CircularProgress } from '@mui/material'
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined'
import { formatDateLocale, reloadPage } from '../../../../../lib/utils.es6'
import useProductCurrency from '../../../../../hooks/useProductCurrency'
import ButtonInc from '../../../../../shared-ui/ButtonInc'
import AlertDialog from '../../../../../components/AlertDialog/index.es6'
import SkeletonWrapper from '../../../../../containers/NewProductList/components/SkeletonWrapper/SkeletonWrapper'
import QuotationDecision from '../QuotationDecision/QuotationDecision'

// A quotation is awaiting a decision (and so shows Approve/Reject) while it is
// in one of these statuses. Once confirmed it becomes Taken_Up/Active; once
// rejected it becomes Rejected — in both cases the actions disappear. Callers
// that omit onDecision get a read-only list.
const PENDING_STATUSES = ['Unconfirmed', 'Pending']

const styles = {
  emptyWrapper: {
    textAlign: 'center',
    padding: '40px 0',
    color: '#6b6b6b',
    fontFamily: 'Open Sans',
    fontSize: '14px'
  },
  errorWrapper: {
    textAlign: 'center',
    padding: '24px',
    color: '#721c24',
    backgroundColor: '#f8d7da',
    borderRadius: '8px',
    fontFamily: 'Open Sans',
    fontSize: '13px'
  },
  card: {
    border: '1px solid #D9D9D9',
    borderRadius: '8px',
    marginBottom: '16px',
    background: '#FFFFFF',
    overflow: 'hidden'
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px'
  },
  iconWrap: {
    background: 'linear-gradient(135deg, #032a37 0%, #173E4B 100%)',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '14px',
    flexShrink: 0
  },
  quotationNumber: {
    fontFamily: 'Lato',
    fontWeight: 600,
    fontSize: '14px',
    color: '#000000'
  },
  quotationSub: {
    fontFamily: 'Open Sans',
    fontSize: '11px',
    color: '#A9A9A9',
    textTransform: 'uppercase',
    letterSpacing: '0.005em'
  },
  pill: {
    display: 'inline-block',
    fontSize: '10px',
    padding: '3px 12px',
    borderRadius: '20px',
    fontWeight: 'bold',
    fontFamily: 'Open Sans'
  },
  pillActive: {
    backgroundColor: '#d4edda',
    color: '#155724'
  },
  pillInactive: {
    backgroundColor: '#e2e3e5',
    color: '#383d41'
  },
  metaLabel: {
    fontFamily: 'Open Sans',
    fontSize: '11px',
    color: '#A9A9A9',
    textTransform: 'uppercase',
    marginBottom: '2px'
  },
  metaValue: {
    fontFamily: 'Lato',
    fontSize: '12px',
    fontWeight: 500,
    color: '#000000'
  },
  metaRow: {
    padding: '0 20px 16px 20px'
  }
}

const QuoteCard = ({ quote, onDecision }) => {
  const currencyCode = useProductCurrency(quote.product_code)

  const premium =
    quote && typeof quote.total_premium_amount_in_cents === 'number'
      ? `${currencyCode} ${(
          quote.total_premium_amount_in_cents / 100
        ).toLocaleString()}`
      : 'N/A'

  const isPending = PENDING_STATUSES.indexOf(quote.status) !== -1

  return (
    <Box sx={styles.card}>
      <Box sx={styles.cardHeader}>
        <Box sx={styles.iconWrap}>
          <NoteAddOutlinedIcon sx={{ color: '#ffffff', fontSize: '18px' }} />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography sx={styles.quotationNumber}>
            {quote.quotation_number || 'N/A'}
          </Typography>
          <Typography sx={styles.quotationSub}>
            {quote.product_name || 'Quotation'}
          </Typography>
        </Box>
        <Box
          component="span"
          sx={{
            ...styles.pill,
            ...(quote.active ? styles.pillActive : styles.pillInactive)
          }}
        >
          {quote.active ? 'Active' : 'Inactive'}
        </Box>
      </Box>

      <Grid container spacing={2} sx={styles.metaRow}>
        <Grid item xs={6} sm={3}>
          <Typography sx={styles.metaLabel}>Start Date</Typography>
          <Typography sx={styles.metaValue}>
            {formatDateLocale(quote.start_date) || 'N/A'}
          </Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography sx={styles.metaLabel}>End Date</Typography>
          <Typography sx={styles.metaValue}>
            {formatDateLocale(quote.end_date) || 'N/A'}
          </Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography sx={styles.metaLabel}>Status</Typography>
          <Typography sx={styles.metaValue}>{quote.status || 'N/A'}</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography sx={styles.metaLabel}>Premium</Typography>
          <Typography sx={styles.metaValue}>{premium}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography sx={styles.metaLabel}>Created At</Typography>
          <Typography sx={styles.metaValue}>
            {formatDateLocale(quote.created_at) || 'N/A'}
          </Typography>
        </Grid>
        {isPending && onDecision && (
          <Grid item xs={12} sx={{ textAlign: 'right' }}>
            <QuotationDecision quote={quote} onDecision={onDecision} />
          </Grid>
        )}
      </Grid>
    </Box>
  )
}

const readError = error => {
  if (!error) return ''
  if (typeof error === 'string') return error
  if (Array.isArray(error.message)) return error.message.join(', ')
  if (error.message) return error.message
  return 'An unexpected error occurred'
}

const PolicyQuotesList = props => {
  if (props.loader) {
    return <SkeletonWrapper loading={props.loader} />
  }

  if (props.error) {
    return (
      <Box sx={styles.errorWrapper}>
        Unable to load quotations. Please try again later.
      </Box>
    )
  }

  const quotes = Array.isArray(props.quotes) ? props.quotes : []

  if (quotes.length === 0) {
    return <Box sx={styles.emptyWrapper}>No quotations found</Box>
  }

  const sorted = quotes
    .slice()
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

  return (
    <Box>
      {sorted.map((quote, index) => (
        <QuoteCard
          key={quote.guid || index}
          quote={quote}
          onDecision={props.onDecision}
        />
      ))}

      <AlertDialog
        custom
        show={props.decisionLoader}
        size="sm"
        style={{ marginTop: '0', top: '30vh' }}
        showCancel={false}
        showConfirm={false}
        showTitle={false}
        confirmBtnCssClass
      >
        <CircularProgress />
        <h2>Recording quotation decision...</h2>
      </AlertDialog>

      <AlertDialog
        success
        show={props.decisionSuccess}
        size="sm"
        title="Quotation decision recorded."
        style={{ marginTop: '0', top: '30vh' }}
        onConfirm={() => reloadPage()}
        confirmBtnText="Ok"
        showCancel={false}
        showConfirm={false}
        confirmBtnCssClass
      >
        <div>
          <ButtonInc
            variant="contained"
            onClick={() => reloadPage()}
            style={{ background: 'green' }}
          >
            Ok
          </ButtonInc>
        </div>
      </AlertDialog>

      <AlertDialog
        show={!!props.decisionError}
        danger
        title="Could not record the quotation decision"
        onConfirm={() => reloadPage()}
        confirmBtnText="Ok"
        confirmBtnCssClass
        showCancel={false}
        style={{ marginTop: '0', top: '30vh' }}
      >
        {readError(props.decisionError)}
      </AlertDialog>
    </Box>
  )
}

export default PolicyQuotesList
